import { SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";
import {
  type TransactionFilters,
  useAccounts,
  useCategories,
  useDeleteTransaction,
  useTransactionPages,
} from "@/api/finance";
import type { Transaction } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, ErrorBanner, Loading } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import { useBreakpoint } from "@/lib/breakpoint";
import { dayLabel, todayCivil } from "@/lib/dates";
import { formatMoney } from "@/lib/money";
import { groupByDay } from "@/lib/transactions";
import { cn } from "@/lib/utils";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { TransactionItem } from "./TransactionItem";

const FILTER_KEYS = ["from", "to", "accountId", "categoryId", "type", "status", "tag", "q"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

function signedTotal(minor: number, currency: string) {
  if (minor === 0) return formatMoney(0, currency);
  return `${minor > 0 ? "+" : "−"} ${formatMoney(Math.abs(minor), currency)}`;
}

export function TransactionsPage() {
  const ws = useWorkspace();
  const canWrite = useCan("transaction:write");
  const mobile = useBreakpoint() === "mobile";
  const [params, setParams] = useSearchParams();
  const filters: TransactionFilters = {};
  for (const k of FILTER_KEYS) {
    const v = params.get(k);
    if (v) filters[k] = v;
  }
  const activeCount = FILTER_KEYS.filter((k) => k !== "q" && filters[k]).length;
  const [panelOpen, setPanelOpen] = useState(false);
  const showPanel = !mobile || panelOpen;
  const [toDelete, setToDelete] = useState<Transaction | null>(null);

  const accounts = useAccounts(ws.id, true);
  const categories = useCategories(ws.id);
  const pages = useTransactionPages(ws.id, filters);
  const remove = useDeleteTransaction(ws.id);
  const today = todayCivil(ws.timezone);

  const setFilter = (key: FilterKey, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const items = pages.data?.pages.flatMap((p) => p.transactions) ?? [];
  const groups = groupByDay(items);

  async function confirmDelete() {
    if (!toDelete) return;
    await remove.mutateAsync(toDelete.id);
    setToDelete(null);
  }

  return (
    <>
      <div className="mb-5 flex items-center justify-between gap-3">
        <h1 className="m-0 text-[28px] font-bold tracking-[-0.03em]">Lançamentos</h1>
        {mobile && (
          <Button
            variant={activeCount ? "primary" : "secondary"}
            aria-expanded={panelOpen}
            onClick={() => setPanelOpen((o) => !o)}
          >
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            {activeCount ? `Filtros (${activeCount})` : "Filtros"}
          </Button>
        )}
      </div>

      <form
        aria-label="Filtros"
        onSubmit={(e) => e.preventDefault()}
        className="mb-5 flex flex-col gap-3 rounded-card bg-surface p-4 shadow-card"
      >
        <TextInput label="Buscar" value={filters.q ?? ""} onChange={(e) => setFilter("q", e.target.value)} />
        {showPanel && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <TextInput
              label="Desde"
              type="date"
              value={filters.from ?? ""}
              onChange={(e) => setFilter("from", e.target.value)}
            />
            <TextInput
              label="Até"
              type="date"
              value={filters.to ?? ""}
              onChange={(e) => setFilter("to", e.target.value)}
            />
            <Select
              label="Conta"
              value={filters.accountId ?? ""}
              onChange={(e) => setFilter("accountId", e.target.value)}
            >
              <option value="">Todas</option>
              {accounts.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            <Select
              label="Categoria"
              value={filters.categoryId ?? ""}
              onChange={(e) => setFilter("categoryId", e.target.value)}
            >
              <option value="">Todas</option>
              {categories.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.parentId ? `— ${c.name}` : c.name}
                </option>
              ))}
            </Select>
            <Select
              label="Tipo"
              value={filters.type ?? ""}
              onChange={(e) => setFilter("type", e.target.value)}
            >
              <option value="">Todos</option>
              <option value="expense">Despesa</option>
              <option value="income">Receita</option>
              <option value="transfer">Transferência</option>
            </Select>
            <Select
              label="Status"
              value={filters.status ?? ""}
              onChange={(e) => setFilter("status", e.target.value)}
            >
              <option value="">Todos</option>
              <option value="cleared">Efetivado</option>
              <option value="pending">Pendente</option>
            </Select>
            <TextInput
              label="Tag"
              value={filters.tag ?? ""}
              onChange={(e) => setFilter("tag", e.target.value)}
            />
          </div>
        )}
        {(activeCount > 0 || filters.q) && (
          <div>
            <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              <X aria-hidden="true" className="size-4" />
              Limpar filtros
            </Button>
          </div>
        )}
      </form>

      {pages.isPending ? (
        <Loading />
      ) : pages.isError ? (
        <ErrorBanner error={pages.error} />
      ) : items.length === 0 ? (
        <EmptyState title="Nenhum lançamento encontrado." />
      ) : (
        <section aria-label="Lançamentos" className="flex flex-col gap-4">
          {groups.map((g) => {
            const label = dayLabel(g.date, today);
            // moeda do workspace primeiro; dia só com transferências mostra zero
            const totals = [...g.totals.entries()].sort(([a], [b]) =>
              a === ws.defaultCurrency ? -1 : b === ws.defaultCurrency ? 1 : a.localeCompare(b),
            );
            if (totals.length === 0) totals.push([ws.defaultCurrency, 0]);
            const total = totals.map(([cur, minor]) => signedTotal(minor, cur)).join(" · ");
            const net = totals[0]?.[1] ?? 0;
            return (
              // biome-ignore lint/a11y/useSemanticElements: agrupa os lançamentos de um dia fora de formulário — <fieldset> é de formulário
              <div
                key={g.date}
                role="group"
                aria-label={`${label} · ${total}`}
                className="rounded-card bg-surface px-4 pt-3 shadow-card"
              >
                <div className="flex items-center justify-between gap-3 border-b border-border pb-2 text-xs font-semibold tracking-wide text-muted uppercase">
                  <h2 className="m-0 text-xs font-semibold">{label}</h2>
                  <span
                    className={cn(
                      "tabular-nums normal-case",
                      net > 0 ? "text-income" : net < 0 ? "text-expense" : "text-muted",
                    )}
                  >
                    {total}
                  </span>
                </div>
                <ul className="m-0 list-none p-0">
                  {g.items.map((tx) => (
                    <TransactionItem
                      key={tx.id}
                      tx={tx}
                      wid={ws.id}
                      accounts={accounts.data}
                      categories={categories.data}
                      onDelete={canWrite ? setToDelete : undefined}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
          {pages.hasNextPage && (
            <div className="text-center">
              <Button
                variant="secondary"
                disabled={pages.isFetchingNextPage}
                onClick={() => void pages.fetchNextPage()}
              >
                Carregar mais
              </Button>
            </div>
          )}
        </section>
      )}

      <Dialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            remove.reset();
          }
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <div className="p-6">
            <DialogTitle>Excluir lançamento?</DialogTitle>
            {toDelete && (
              <p className="mt-3 text-[15px] text-muted">
                <strong className="text-ink">{toDelete.description}</strong> ·{" "}
                {formatMoney(toDelete.amountMinor, toDelete.currency)} ·{" "}
                {toDelete.date.split("-").reverse().join("/")}
                {toDelete.type === "transfer" && " — as duas pontas da transferência serão excluídas."}
              </p>
            )}
            <div className="mt-2">
              <ErrorBanner error={remove.error} />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setToDelete(null);
                  remove.reset();
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() => void confirmDelete().catch(() => undefined)}
              >
                Excluir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
